import {type CSSProperties, useEffect, useMemo, useRef, useState} from 'react'
import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import {mintUnsafeStaticHTML} from '@github-ui/trusted-types/unsafe-static'
import {
  type TuiCapClass,
  type TuiCapData,
  type TuiCapOptions,
  createPlayer,
  renderPlaceholderHTML,
  renderPosterHTML,
  rootStyle,
} from './tuicap-engine'
import styles from './TuiCapPlayer.module.css'
const CLASSES = styles as unknown as Partial<Record<TuiCapClass, string>>

export interface TuiCapPlayerProps extends Omit<TuiCapOptions, 'classes'> {
  /** Recording data (an imported .tuicap.json). */
  data?: TuiCapData
  /** Lazy loader, e.g. `() => import('./demo.tuicap.json')`; runs when the player nears the viewport. */
  load?: () => Promise<TuiCapData | {default: TuiCapData}>
  /** Grid size used to reserve space before lazily loaded data arrives. */
  cols?: number
  rows?: number
  /** Monospace font stack. Defaults to the page's --custom-fontStack-monospace. */
  fontFamily?: string
  /** The font's advance width in em (used to fit the grid to its container). Default 0.6. */
  advance?: number
  className?: string
  'aria-label'?: string
}

/**
 * Plays a tui-cap terminal recording, then lets the viewer scroll the
 * transcript like a live CLI session. Server-renders the first frame (or a
 * size-reserving placeholder while lazily loading), so there is no layout
 * shift. Options are read when the player mounts.
 */
export function TuiCapPlayer(props: TuiCapPlayerProps) {
  const {data: initialData, load, cols, rows, fontFamily, advance, className, ...options} = props
  const ref = useRef<HTMLDivElement>(null)
  const [loadedData, setLoadedData] = useState<TuiCapData | null>(null)
  const data = initialData ?? loadedData
  // Options are read once on mount, as documented, so the player isn't recreated by new prop identities.
  const [engineOptions] = useState<TuiCapOptions>(() => ({...options, classes: CLASSES}))
  const markup = useMemo(
    () => mintUnsafeStaticHTML(data ? renderPosterHTML(data, engineOptions) : renderPlaceholderHTML(engineOptions)),
    [data, engineOptions],
  )

  useEffect(() => {
    const node = ref.current
    if (data || !load || !node) return
    let cancelled = false
    const start = async () => {
      try {
        const mod = await load()
        if (!cancelled) setLoadedData('default' in mod ? mod.default : mod)
      } catch {
        // Keep showing the placeholder if the recording fails to load.
      }
    }
    if (typeof IntersectionObserver !== 'function') {
      // Fetching the recording is the only effect work; the result is kept in state.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-external-store-subscription
      start()
      return () => {
        cancelled = true
      }
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          io.disconnect()
          start()
        }
      },
      {rootMargin: '400px 0px'},
    )
    io.observe(node)
    return () => {
      cancelled = true
      io.disconnect()
    }
  }, [data, load])

  useEffect(() => {
    const node = ref.current
    if (!data || !node) return
    const player = createPlayer(node, data, engineOptions)
    return () => player.destroy()
  }, [data, engineOptions])

  const style = {
    ...(data
      ? rootStyle(data, engineOptions)
      : {
          '--tuicap-cols': String(cols ?? 120),
          '--tuicap-rows': String(rows ?? 32),
          '--tuicap-lh': String(engineOptions.lineHeight ?? 1.2),
        }),
    ...(fontFamily ? {'--tuicap-font': fontFamily} : {}),
    ...(advance ? {'--tuicap-advance': String(advance)} : {}),
  } as CSSProperties

  return (
    <>
      <VerifiedHTMLDiv
        ref={ref}
        className={[CLASSES.root, className].filter(Boolean).join(' ')}
        style={style}
        role="region"
        aria-label={props['aria-label'] ?? 'Terminal recording'}
        html={markup}
      />
    </>
  )
}
